import { API_ENDPOINT } from '../config'
import { ArchiveSortOption } from '../types'
import { EVENTS } from '../config'
import apiFetch from '../util/apiFetch'

type Callback = (...args: unknown[]) => void

export const DEFAULT_SETTINGS = {
	showclock: false,
	darkmode: null, // null = auto (follow system), true = dark, false = light
	archiveSortOption: 'number-asc',
}

const THEME_COLORS = {
	light: '#38849e',
	dark: '#0b2126',
}

export class Settings {
	eventHandlers: Record<string, Callback[]> = {}
	showclock: boolean
	darkmode: boolean | null
	archiveSortOption: ArchiveSortOption
	private systemPrefersDark: MediaQueryList

	constructor() {
		this.eventHandlers = {}
		Object.keys(DEFAULT_SETTINGS).forEach(key => {
			this[key] = DEFAULT_SETTINGS[key]
		})

		// Detect system preference
		this.systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)')

		// Listen for system preference changes
		this.systemPrefersDark.addEventListener('change', () => {
			// Only apply system changes if user hasn't set a preference
			if (this.darkmode === null) {
				this.applyDarkMode()
				this.emit(EVENTS.SETTINGS_CHANGED)
			}
		})

		this.load()
	}

	load(): void {
		const str = localStorage.getItem('settings') || '{}'
		const settings = JSON.parse(str)
		if (settings) {
			this.showclock = settings.showclock || this.showclock
			this.darkmode = settings.darkmode !== undefined ? settings.darkmode : this.darkmode
			this.archiveSortOption = settings.archiveSortOption ?? this.archiveSortOption
			this.emit(EVENTS.SETTINGS_CHANGED)
		}

		this.applyDarkMode()
	}

	save(): void {
		const simpleObj = JSON.parse(JSON.stringify(this))
		delete simpleObj.eventHandlers
		delete simpleObj.systemPrefersDark
		localStorage.setItem('settings', JSON.stringify(simpleObj))
	}

	setShowClock(showclock: boolean): void {
		this.showclock = showclock
		this.save()
		this.emit(EVENTS.SETTINGS_CHANGED)
		try {
			const url = `${API_ENDPOINT}/log`
			apiFetch(url, 'POST', { message: `Show clock: ${showclock}` })
		} catch (e) {
			console.error(e)
		}
	}

	getShowClock(): boolean {
		return this.showclock
	}

	setDarkMode(darkmode: boolean | null): void {
		this.darkmode = darkmode
		this.save()
		this.applyDarkMode()
		this.emit(EVENTS.SETTINGS_CHANGED)
		try {
			const url = `${API_ENDPOINT}/log`
			const mode = darkmode === null ? 'auto' : darkmode ? 'dark' : 'light'
			apiFetch(url, 'POST', { message: `Dark mode: ${mode}` })
		} catch (e) {
			console.error(e)
		}
	}

	setArchiveSortOption(archiveSortOption: ArchiveSortOption): void {
		this.archiveSortOption = archiveSortOption
		this.save()
		this.emit(EVENTS.SETTINGS_CHANGED)
	}

	getDarkMode(): boolean | null {
		return this.darkmode
	}

	getEffectiveDarkMode(): boolean {
		if (this.darkmode === null) {
			// Auto mode - follow system preference
			return this.systemPrefersDark.matches
		}
		return this.darkmode
	}

	applyDarkMode(): void {
		const isDark = this.getEffectiveDarkMode()

		if (isDark) {
			document.documentElement.classList.add('dark-mode')
		} else {
			document.documentElement.classList.remove('dark-mode')
		}

		// Update theme-color meta tag
		this.updateThemeColor(isDark)
	}

	updateThemeColor(isDark: boolean): void {
		const metaThemeColor = document.querySelector('meta[name="theme-color"]')
		const themeColor = isDark ? THEME_COLORS.dark : THEME_COLORS.light

		if (metaThemeColor) {
			metaThemeColor.setAttribute('content', themeColor)
		}
	}

	// -----------------------------------------------------------------------------------------------

	on(event: string, callback: Callback) {
		this.eventHandlers[event] = this.eventHandlers[event] || []
		this.eventHandlers[event].push(callback)
	}

	off(event: string, callback: Callback) {
		if (this.eventHandlers[event]) {
			this.eventHandlers[event] = this.eventHandlers[event].filter(
				handler => handler !== callback
			)
		}
	}

	emit(event: string, ...args: unknown[]) {
		if (this.eventHandlers[event]) {
			this.eventHandlers[event].forEach(handler => handler(...args))
		}
	}
}

const SETTINGS = new Settings()

export default SETTINGS
